import React, { useRef, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../Interview/video_chat.module.scss";
import Images from "../../../../assets/Images";
import axios from "axios";
import CodeEditor from "../Interview/codeEditor";
import { Dialog } from "@mui/material";
import InterviewEvaluation from "../Interview/interviewEvaluation";
import { APIConstant } from "services/api-constant";
import JitsiSession from "./JitsiSession";
import { SESSION_TYPES } from "./sessionTypes";
import Popup from "components/minor/popup/popup";
import SessionDock, { DockPanelHeader } from "components/minor/session-dock/session-dock";
import { MessageSquare, ListChecks, Code as CodeIcon, Sparkles, Loader2 } from "lucide-react";

const dockButtonStyle = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  background: "linear-gradient(135deg,#00C8FF,#7C5CFF)", border: "none", borderRadius: 8,
  color: "#fff", fontFamily: "Outfit,sans-serif", fontSize: 12.5, fontWeight: 700,
  padding: "9px 16px", cursor: "pointer",
};

import { apiBaseUrl } from '../../../../services/config';
const API_BASE = apiBaseUrl;
const useQuery  = () => new URLSearchParams(useLocation().search);

// ── Speech Recognition factory (cross-browser) ──────────────────
const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

// Interview type — lifted from Interview/video_chat.jsx unchanged. Only the
// Jitsi mount was swapped for the shared JitsiSession wrapper (configured
// from sessionTypes.js's "interview" entry, which preserves the exact
// restricted-toolbar/auto-open-chat/dominant-speaker-tracking behavior the
// old Interview/JitsiMeeting.jsx had) — everything else (speech recognition,
// question scoring, evaluation, code editor) is untouched.
const InterviewBody = () => {
  const navigate      = useNavigate();
  const query         = useQuery();
  const user          = query.get("clientId");
  const techId        = query.get("techId");
  const interviewId   = query.get("interviewId");

  // ── Refs ─────────────────────────────────────────────────────
  const localVideo             = useRef();
  const remoteVideo            = useRef();
  const canvasRef              = useRef(null);
  const audioRef               = useRef(null);
  const chatEndRef             = useRef(null);
  const selectedQuestionsRef   = useRef([]);
  const recognitionRef         = useRef(null);
  const currentTranscriptRef   = useRef("");   // accumulates text for the CURRENT question
  const mediaRecorderRef       = useRef(null); // records remote audio as backup
  const audioChunksRef         = useRef([]);
  const intervalRef            = useRef(null);

  // ── State ─────────────────────────────────────────────────────
  const [open,               setOpen]               = React.useState(false);
  const [clientId,           setClientId]           = useState("");
  const [remoteId,           setRemoteId]           = useState("");
  const [connected,          setConnected]          = useState(false);
  const [micOn,              setMicOn]              = useState(true);
  const [cameraOn,           setCameraOn]           = useState(true);
  const [messages,           setMessages]           = useState([]);
  const [chatInput,          setChatInput]          = useState("");
  const [code,               setCode]               = useState("// Start coding JavaScript here...");

  // Questions
  const [allQuestions,       SetAllQuestions]       = useState([]);
  const [questionLen,        SetQuestionLen]        = useState(0);
  const [selectedQuestions,  SetSelectedQuestions]  = useState([]);
  const [startQuestions,     setStartQuestions]     = useState(false);
  const [showScore,          setShowScore]          = useState(false);
  const [loading,            setLoading]            = useState(false);
  const [retryCount,         setRetryCount]         = useState(0);

  // Transcript (for live display in the chat panel)
  const [lines,              setLines]              = useState([]);

  // Analysis
  const [lastQuestionProcessed, setLastQuestionProcessed] = useState(true);
  const [analysisLoading,    setAnalysisLoading]    = useState(false);
  const [evaluations,        setEvaluations]        = useState([]);

  // Modals
  const [openEvaluation,     setOpenEvaluation]     = React.useState(false);
  const [openSuccessPopup,   setOpenSuccessPopup]   = useState(false);
  const [interviewerStatus,  setInterviewerStatus]  = useState("");

  // Jitsi / layout
  const [questionWindowOn,   setQuestionWindowOn]   = useState(false);
  const [participant,        setParticipant]        = useState(null);
  const [remoteStream,       setRemoteStream]       = useState(null);
  const [participantsCount,  setParticipantsCount]  = useState(0);
  const [allParticipants,    setAllParticipants]    = useState([]);

  // Live-session tool dock
  const [dockOpen,           setDockOpen]           = useState(true);
  const [activeTool,         setActiveTool]         = useState("transcript");

  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2,"0")}:${now.getMinutes().toString().padStart(2,"0")}`;
  };

  // ════════════════════════════════════════════════════════════════
  // SPEECH RECOGNITION — continuous, accumulates per question
  // Captures: interviewer mic + candidate audio (if on speakers)
  // ════════════════════════════════════════════════════════════════
  const startSpeechRecognition = useCallback(() => {
    if (!SpeechRecognition) {
      console.warn("SpeechRecognition not supported in this browser");
      return;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(e) {}
    }

    const rec            = new SpeechRecognition();
    rec.continuous       = true;
    rec.interimResults   = true;
    rec.lang             = "en-US";
    rec.maxAlternatives  = 1;

    let finalBuffer = "";

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalBuffer += text + " ";
          // Accumulate locally
          currentTranscriptRef.current += text + " ";
          // Show in transcript panel with speaker label
          const speaker = user === "interviewer" ? "Interviewer" : "Candidate";
          setLines(prev => [...prev, {
            by:   speaker,
            text: text.trim(),
            time: new Date().toISOString(),
          }]);
          // POST this side's speech to backend for merging
          if (interviewId) {
            const authH = { Authorization: `Bearer ${sessionStorage.getItem("platform_token")||""}` };
            axios.post(`${API_BASE}/submit-transcript`, {
              interview_id: interviewId,
              role:         user === "interviewer" ? "interviewer" : "candidate",
              text:         text.trim(),
            }).catch(e => console.warn("submit-transcript:", e.message));
          }
        } else {
          interim = text;
        }
      }
    };

    rec.onerror = (e) => {
      console.error("SpeechRecognition error:", e.error);
      // Auto-restart on recoverable errors
      if (["no-speech","audio-capture","network"].includes(e.error)) {
        setTimeout(() => startSpeechRecognition(), 1500);
      }
    };

    rec.onend = () => {
      // Auto-restart if still in interview
      if (startQuestions && !showScore) {
        try { rec.start(); } catch(e) {}
      }
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      console.log("✅ SpeechRecognition started");
    } catch(e) {
      console.error("SpeechRecognition start failed:", e);
    }
  }, [startQuestions, showScore]);

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(e) {}
      recognitionRef.current = null;
    }
  };

  // ════════════════════════════════════════════════════════════════
  // REMOTE AUDIO RECORDING — records candidate audio as backup blob
  // Sent to transcription if speech recognition is insufficient
  // ════════════════════════════════════════════════════════════════
  const startRemoteRecording = useCallback((stream) => {
    if (!stream) return;
    audioChunksRef.current = [];

    try {
      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/wav",
      });
      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.start(1000); // collect in 1s chunks
      mediaRecorderRef.current = recorder;
      console.log("✅ Remote audio recording started");
    } catch(e) {
      console.error("Remote recording failed:", e);
    }
  }, []);

  const stopRemoteRecording = () => {
    return new Promise(resolve => {
      if (!mediaRecorderRef.current ||
          mediaRecorderRef.current.state === "inactive") {
        resolve(null);
        return;
      }
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: mediaRecorderRef.current.mimeType || "audio/webm",
        });
        audioChunksRef.current = [];
        resolve(blob.size > 1000 ? blob : null);
      };
      try { mediaRecorderRef.current.stop(); } catch(e) { resolve(null); }
    });
  };

  const resetRecordingForNextQuestion = (stream) => {
    // Stop old recording, reset transcript buffer, start fresh
    stopRemoteRecording().then(() => {
      audioChunksRef.current = [];
      currentTranscriptRef.current = "";
      if (stream) startRemoteRecording(stream);
    });
    setLines([]);
  };

  // ════════════════════════════════════════════════════════════════
  // QUESTIONS — load from new endpoint, fallback to legacy
  // ════════════════════════════════════════════════════════════════
  const getQuestions = async (tech) => {
    try {
      setLoading(true);
      let questions = [];

      // Try new no-auth endpoint first
      try {
        const res = await axios.get(`${API_BASE}/interviews-v2/questions/public/${tech}`);
        questions  = res.data || [];
        console.log(`[questions] ${questions.length} loaded from new endpoint`);
      } catch(e) {
        console.warn("[questions] New endpoint failed:", e.message);
      }

      // Fallback: legacy /questions/{techId} — fix double-slash bug
      if (questions.length === 0) {
        try {
          const base = (APIConstant.getInterviewQuestion || "").replace(/\/+$/, "");
          const res  = await axios.get(`${base}/${tech}`);
          questions  = (res.data || []).map(q => ({
            ...q,
            text:           q.text           || q.question        || "",
            correct_answer: q.correct_answer || q.expected_answer || "",
          }));
          console.log(`[questions] ${questions.length} loaded from legacy endpoint`);
        } catch(e2) {
          console.error("[questions] Both endpoints failed:", e2);
        }
      }

      SetAllQuestions(questions);
      SetQuestionLen(questions.length);
      if (questions.length > 0) SetSelectedQuestions([questions[0]]);
    } catch(err) {
      console.error("Failed to fetch questions:", err);
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════
  // NEXT QUESTION — collect full transcript → send → get feedback
  // ════════════════════════════════════════════════════════════════
  const goToNextQuestion = async () => {
    setLastQuestionProcessed(false);
    setAnalysisLoading(true);

    const currentQuestion = selectedQuestionsRef.current[selectedQuestionsRef.current.length - 1];
    if (!currentQuestion) {
      setLastQuestionProcessed(true);
      setAnalysisLoading(false);
      return;
    }

    // ── Step 1: collect transcript ──────────────────────────────
    // Primary: local speech recognition text (this side's mic)
    let candidateText = currentTranscriptRef.current.trim();

    // Also fetch the other side's transcript from backend and merge
    if (interviewId) {
      try {
        const mergedRes  = await axios.get(`${API_BASE}/merged-transcript/${interviewId}`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token")||""}` }
        });
        const serverSide = user === "interviewer"
          ? (mergedRes.data?.candidate    || "")   // interviewer wants candidate's answer
          : (mergedRes.data?.interviewer  || "");  // candidate view: interviewer text
        if (serverSide) {
          // Prefer server-side candidate transcript (the other mic)
          candidateText = serverSide;
        }
      } catch(e) {
        console.warn("merged-transcript fetch failed:", e.message);
        // Fall back to local SpeechRecognition text
      }
    }

    console.log("[answer transcript]", candidateText || "(empty — no speech detected)");

    // ── Step 2: screenshot ──────────────────────────────────────
    const capturedImage = takeScreenshot();

    // ── Step 3: send to AI analysis API ────────────────────────
    try {
      const payload = {
        question:       currentQuestion.text           || "",
        correct_answer: currentQuestion.correct_answer || "",
        transcript:     candidateText,
        image_64:       capturedImage || "",
      };

      // Strip trailing slash — our route handles both with and without
      const analyzeUrl = (APIConstant.analyze || `${API_BASE}/analyze`).replace(/\/+$/, "");
      const response   = await axios.post(analyzeUrl, payload);
      const result   = response.data;

      // Store all AI results on the question object
      currentQuestion.sentiment    = result.sentiment    || "";
      currentQuestion.confidence   = result.confidence   || "";
      currentQuestion.evaluation   = result.evaluation   || "";
      currentQuestion.emotion      = result.emotion      || "";
      currentQuestion.feedback     = result.feedback     || result.evaluation || "";
      currentQuestion.transcript   = candidateText;
      currentQuestion.question     = currentQuestion.text;
      currentQuestion.time         = getCurrentTime();

      setEvaluations([...selectedQuestionsRef.current]);
      console.log("✅ Analysis result:", result);
    } catch(err) {
      console.error("Analysis API failed:", err);
      currentQuestion.transcript = candidateText;
      currentQuestion.time       = getCurrentTime();
      setEvaluations([...selectedQuestionsRef.current]);
    }

    // ── Step 4: advance to next question or show final score ────
    if (allQuestions.length > selectedQuestions.length) {
      const next = allQuestions.slice(selectedQuestions.length, selectedQuestions.length + 1);
      SetSelectedQuestions(prev => [...prev, ...next]);

      // Reset transcript and recording for next question
      resetRecordingForNextQuestion(remoteStream);
      // Clear backend transcript store for this question
      if (interviewId) {
        axios.delete(`${API_BASE}/clear-transcript/${interviewId}`)
          .catch(e => console.warn("clear-transcript:", e.message));
      }
    } else {
      // Interview complete
      stopSpeechRecognition();
      await stopRemoteRecording();
      setShowScore(true);
    }

    setLastQuestionProcessed(true);
    setAnalysisLoading(false);
  };

  const saveInterviewProgress = () => setEvaluations([...selectedQuestionsRef.current]);

  // ════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ════════════════════════════════════════════════════════════════
  useEffect(() => { selectedQuestionsRef.current = selectedQuestions; }, [selectedQuestions]);

  useEffect(() => { getQuestions(techId); }, []);

  useEffect(() => {
    if (questionLen === 0 && retryCount < 3) {
      const timer = setTimeout(() => {
        getQuestions(techId);
        setRetryCount(p => p + 1);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [questionLen, retryCount]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [lines]);

  // Start recording + recognition when interview has 2+ participants
  useEffect(() => {
    if (participantsCount >= 2 && startQuestions) {
      startSpeechRecognition();
      if (remoteStream) startRemoteRecording(remoteStream);
    }
  }, [participantsCount, startQuestions, remoteStream]);

  // When remote stream arrives, start recording it
  useEffect(() => {
    if (remoteStream && audioRef.current) {
      audioRef.current.srcObject = remoteStream;
    }
    if (remoteStream && startQuestions && participantsCount >= 2) {
      startRemoteRecording(remoteStream);
    }
  }, [remoteStream]);

  // Peer connection for local video
  useEffect(() => {
    const params        = new URLSearchParams(window.location.search);
    const clientFromURL = params.get("clientId");
    const roomFromURL   = params.get("roomId");
    const tokenFromURL  = params.get("token");
    if (clientFromURL) setClientId(clientFromURL);
    if (roomFromURL)   setRemoteId(roomFromURL);
    // Persist token from URL into sessionStorage so API calls in this tab work
    if (tokenFromURL) {
      sessionStorage.setItem("platform_token", tokenFromURL);
      localStorage.setItem("platform_token",   tokenFromURL);
    }
    if (!clientId || connected) return;

    navigator.mediaDevices.getUserMedia({ video: true, audio: true }).then(stream => {
      localVideo.current.srcObject = stream;
      setConnected(true);
    }).catch(e => console.error("Media access failed:", e));
  }, [clientId, remoteId, connected]);

  useEffect(() => () => {
    stopSpeechRecognition();
    stopRemoteRecording();
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  // JitsiSession calls this unconditionally on every mouse move over the video
  // area — kept as a required prop even though nothing here reacts to it now
  // that the old toggle-button auto-hide behavior is gone (see SessionDock).
  const handleMouseMoveEvent = () => {};

  // ════════════════════════════════════════════════════════════════
  // HELPERS
  // ════════════════════════════════════════════════════════════════
  const takeScreenshot = () => {
    const video = localVideo.current;
    if (!video) return null;
    const canvas = document.createElement("canvas");
    canvas.width  = video.videoWidth  || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg");
  };

  const toggleMic = () => {
    const track = localVideo.current?.srcObject?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
  };

  const toggleCamera = () => {
    const track = localVideo.current?.srcObject?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setCameraOn(track.enabled); }
  };

  const handleClickOpen        = () => setOpen(true);
  const handleClose            = () => setOpen(false);
  const handleClickOpenEval    = () => setOpenEvaluation(true);
  const handleCloseEvaluation  = () => setOpenEvaluation(false);
  const handleSuccessPopup     = () => setOpenSuccessPopup(true);
  const handleClosePopup       = () => setOpenSuccessPopup(false);
  const handlePopup = () => {
    setOpenSuccessPopup(false);
    setTimeout(() => navigate(`/admin/interviews/list/${techId}/${interviewerStatus}`), 1500);
  };

  const handleChildClick = (name) => {
    setParticipant(name);
    setQuestionWindowOn(true);
  };

  // ════════════════════════════════════════════════════════════════
  // EMPTY STATE
  // ════════════════════════════════════════════════════════════════
  if (!clientId || !remoteId) {
    return (
      <div className="h-screen w-screen bg-gray-950 text-white flex flex-col items-center justify-center gap-4 p-6">
        <h1 className="text-2xl font-bold mb-4">Enter your details to start a call</h1>
        <input type="text" placeholder="Your ID" value={clientId}
          onChange={e => setClientId(e.target.value)}
          className="p-2 rounded-md bg-gray-800 w-full max-w-sm focus:outline-none"/>
        <input type="text" placeholder="Remote ID to connect" value={remoteId}
          onChange={e => setRemoteId(e.target.value)}
          className="p-2 rounded-md bg-gray-800 w-full max-w-sm focus:outline-none"/>
        <button onClick={() => setConnected(false)}
          className="mt-2 bg-blue-600 hover:bg-blue-500 px-6 py-2 rounded-md">
          Connect
        </button>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // RENDER — original UI preserved exactly
  // ════════════════════════════════════════════════════════════════
  const jitsi = SESSION_TYPES.interview.jitsi;
  return (
    <div className={styles.appContainer}>

      {/* Hidden video/audio elements */}
      <div className={`${styles.videoControlWrapper} flex-1 relative flex flex-col items-center justify-center px-6 py-8`}>
        <div className="w-full h-screen">
          <video style={{ display:"none" }} ref={localVideo} muted autoPlay playsInline className="w-full h-full object-cover"/>
          <audio ref={audioRef} autoPlay style={{ display:"none" }}/>
        </div>
      </div>

      <div className={`${styles.videoContainer} ${(participantsCount > 2 && questionWindowOn && user !== "interviewer") ? "shortVideoContainer" : "fluidVideoContainer"}`}>

        <JitsiSession
          roomName={remoteId}
          scriptSrc={jitsi.scriptSrc}
          configOverwrite={jitsi.configOverwrite}
          interfaceConfigOverwrite={jitsi.interfaceConfigOverwrite}
          autoOpenChat={jitsi.autoOpenChat}
          autoOpenParticipants={jitsi.autoOpenParticipants}
          mouseEventTriggered={handleMouseMoveEvent}
          onRemoteAudio={setRemoteStream}
          onButtonClick={handleChildClick}
          onSpeakerChange={() => {}}
          chatOpen={!(dockOpen && activeTool === "transcript")}
          onParticipantsJoin={setParticipantsCount}
          onParticipantsListChange={setAllParticipants}
          onLeave={() => navigate("/meetingended")}
        />

        {/* ── Live-session tool dock — Transcript (everyone), Questions & Scoring
             (interviewer only), Code Editor (opens its own Dialog — a code
             editor needs real width, not a 320px drawer) ── */}
        {questionWindowOn && (
          <SessionDock
            open={dockOpen}
            onToggleOpen={setDockOpen}
            activeTool={activeTool}
            onSelectTool={(key) => setActiveTool((prev) => (prev === key ? null : key))}
            collapsedIcon={MessageSquare}
            drawerWidth={340}
            tools={[
              { key: "transcript", icon: MessageSquare, label: "Transcript" },
              ...(user === "interviewer" ? [{ key: "questions", icon: ListChecks, label: "Questions & Scoring" }] : []),
              { key: "code", icon: CodeIcon, label: "Code Editor", standalone: true, onClick: handleClickOpen },
            ]}
          >
            {activeTool === "transcript" && (
              <>
                <DockPanelHeader icon={MessageSquare} title="Transcript" onClose={() => setActiveTool(null)} />
                <div className="sdock-scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 18px" }}>
                  {lines.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#3D5473", fontSize: 12.5, marginTop: 40 }}>
                      <Sparkles size={20} style={{ marginBottom: 8, opacity: 0.6 }} />
                      <div>🎙️ Waiting for speech from {participant || "Candidate"}… (allow microphone access)</div>
                    </div>
                  ) : lines.map((line, i) => {
                    const fmt = (t) => {
                      const d = new Date(t);
                      if (isNaN(d)) return "";
                      return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}, ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
                    };
                    const prev       = lines[i - 1];
                    const showHeader = !prev || prev.by !== line.by || fmt(prev.time) !== fmt(line.time);
                    return (
                      <div key={`line-${i}`} className="sdock-line" style={{ marginBottom: 10 }}>
                        {showHeader && (
                          <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 2 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#00C8FF" }}>{line.by}</span>
                            <span style={{ fontSize: 10, color: "#3D5473" }}>{fmt(line.time)}</span>
                          </div>
                        )}
                        <div style={{ fontSize: 13, color: "#E8F0FE", lineHeight: 1.5 }}>{line.text}</div>
                      </div>
                    );
                  })}
                  <div ref={chatEndRef}/>
                </div>
                <div style={{ padding: "10px 18px", borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 10.5, color: "#3D5473", textAlign: "center" }}>
                  Use the Jitsi chat in the meeting panel for text chat
                </div>
              </>
            )}

            {activeTool === "questions" && user === "interviewer" && (
              <>
                <DockPanelHeader icon={ListChecks} title="Questions & Scoring" onClose={() => setActiveTool(null)} />
                <div className="sdock-scroll" style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>
                  {!startQuestions && !loading && (
                    <div style={{ textAlign: "center" }}>
                      <p style={{ fontSize: 12.5, color: "#C8D6E8", marginBottom: 14, lineHeight: 1.6 }}>
                        Click <b>Start Question</b> to begin the session
                      </p>
                      <button style={dockButtonStyle} onClick={() => {
                        setStartQuestions(true);
                        currentTranscriptRef.current = "";
                        setLines([]);
                        startSpeechRecognition();
                        if (remoteStream) startRemoteRecording(remoteStream);
                      }}>Start Question</button>
                      {!SpeechRecognition && (
                        <p style={{ fontSize: 11, color: "#FFB800", marginTop: 10 }}>⚠️ Use Chrome for speech recognition</p>
                      )}
                    </div>
                  )}

                  {!startQuestions && loading && (
                    <div style={{ display: "flex", justifyContent: "center", padding: 30 }}>
                      <Loader2 size={22} color="#00C8FF" className="sdock-spin" />
                    </div>
                  )}

                  {!loading && startQuestions && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {selectedQuestionsRef.current.map((item, index) => (
                          <div key={`question-${index}`} className="sdock-line"
                            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 12px" }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#E8F0FE", marginBottom: 8, lineHeight: 1.5 }}>{item?.text}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <div><div style={{ fontSize: 10, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Confidence</div><div style={{ fontSize: 11.5, color: "#C8D6E8", marginTop: 2 }}>{item?.confidence || "—"}</div></div>
                              <div><div style={{ fontSize: 10, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Emotion</div><div style={{ fontSize: 11.5, color: "#C8D6E8", marginTop: 2 }}>{item?.emotion || "—"}</div></div>
                              <div><div style={{ fontSize: 10, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Engagement</div><div style={{ fontSize: 11.5, color: "#C8D6E8", marginTop: 2 }}>{item?.sentiment || "—"}</div></div>
                              <div><div style={{ fontSize: 10, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em" }}>Evaluation</div><div style={{ fontSize: 11.5, color: "#C8D6E8", marginTop: 2 }}>{item?.evaluation || "—"}</div></div>
                            </div>
                            {item?.feedback && (
                              <div style={{ marginTop: 8, fontSize: 11.5, color: "#00C8FF", lineHeight: 1.5 }}>
                                <strong>Feedback:</strong> {item.feedback}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {analysisLoading && (
                        <div style={{ padding: "14px 0", display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#00C8FF" }}>
                          <Loader2 size={14} className="sdock-spin" /> Analysing answer…
                        </div>
                      )}

                      {!showScore && (
                        <div style={{ paddingTop: 14 }}>
                          <button style={{ ...dockButtonStyle, width: "100%", opacity: (!lastQuestionProcessed || analysisLoading) ? 0.6 : 1 }}
                            onClick={goToNextQuestion} disabled={!lastQuestionProcessed || analysisLoading}>
                            Next {(!lastQuestionProcessed || analysisLoading) && <Loader2 size={13} className="sdock-spin" />}
                          </button>
                        </div>
                      )}

                      {showScore && (
                        <div style={{ paddingTop: 14 }}>
                          <button style={{ ...dockButtonStyle, width: "100%" }} onClick={handleClickOpenEval}>Evaluate</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </SessionDock>
        )}

        {/* Evaluation dialog */}
        <Dialog
          open={openEvaluation}
          onClose={handleCloseEvaluation}
          sx={{
            "& .MuiDialog-paper": {
              background: "transparent",
              borderRadius: "14px",
              boxShadow: "none",
            },
            "& .MuiBackdrop-root": {
              background: "rgba(0,0,0,0.75)",
              backdropFilter: "blur(4px)",
            }
          }}>
          <InterviewEvaluation
            handleSuccessPopup={handleSuccessPopup}
            handleCloseEvaluation={handleCloseEvaluation}
            fullTranscript={lines}
            questionLen={questionLen}
            evaluations={evaluations}
            interviewId={interviewId}
            setInterviewerStatus={setInterviewerStatus}
          />
        </Dialog>

        <Popup
          open={openSuccessPopup}
          message="Your request has been submitted"
          title="Thank You"
          handleClose={handleClosePopup}
          handleConfirm={handlePopup}
          type="success"
        />

      </div>

      {/* Code editor dialog */}
      <Dialog open={open} onClose={handleClose}>
        <div className={styles.liveCodeEditor}>
          <div className={styles.header}>
            <h2>Live Code Editor</h2>
            <a onClick={handleClose} style={{ cursor:"pointer" }}>
              <img src={Images.CLOSE} width="14"/>
            </a>
          </div>
          <CodeEditor initialDoc={code} onChange={val => setCode(val)}/>
        </div>
      </Dialog>
    </div>
  );
};

export default InterviewBody;
